import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CardMedia,
  Dialog,
  IconButton,
  Skeleton,
  Tab,
  useMediaQuery,
  Grid,
} from '@mui/material'
import DefaultEventImage from '../../../../../assets/images/DefaultEventImage.png'
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded'
import CloseIcon from '@mui/icons-material/Close'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { useTheme } from '@mui/system'
import { useFetchImageUrl } from '../../../../../hooks/componentHooks/useFetchImageUrl'
import { textColors } from '../../../../Common/Color/Color'
import { fontSizes, fontWeights } from '../../../../Common/Typography/TypographyStyles'

import EventDetailsTab from './EventViewTabPanel/EventDetailsTab'
import EventShareTab from './EventViewTabPanel/EventShareTab'
import { useNavigate } from 'react-router'
import { EventWithAdditionalInfo } from '../../../../../redux/event/teamMemberEvents/types'
import { fetchTeamMemberInfo } from '../../../../../redux/teamMember/actions'
import { useAppDispatch } from '../../../../../redux/store'
import { clearTeamMemberInfo } from '../../../../../redux/teamMember/slice'

interface CustomizeTabPanelProps {
  value: string
  children: React.ReactNode
  fullScreen: boolean
}

const CustomizeTabPanel: React.FC<CustomizeTabPanelProps> = ({ value, children, fullScreen }) => {
  return (
    <TabPanel value={value}>
      <Box
        sx={{
          ...(!fullScreen && {
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#5f2b7f',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#555',
            },
            scrollbarWidth: 'auto',
            msOverflowStyle: 'auto',
          }),
        }}
      >
        {children}
      </Box>
    </TabPanel>
  )
}

const DialogSkeleton = () => {
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Box position='relative' sx={{ height: '500px', overflow: 'hidden' }}>
          <Skeleton variant='rectangular' sx={{ height: '100%', objectFit: 'cover' }} />
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
          }}
          size='small'
        >
          <CloseIcon />
        </IconButton>
        <Box p={2}>
          <Skeleton variant='rectangular' width={120} height={40} sx={{ borderRadius: '4px' }} />
        </Box>
        <Box p={2}>
          <Skeleton variant='rectangular' height={40} sx={{ mb: 3 }} />
          <Skeleton variant='rectangular' height={300} />
        </Box>
      </Grid>
    </Grid>
  )
}

interface EventViewModalProps {
  eventData: EventWithAdditionalInfo
  openModal?: boolean
}

const EventViewModal: React.FC<EventViewModalProps> = ({ eventData, openModal }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const navigate = useNavigate()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [open, setOpen] = useState(false)
  const [value, setValue] = React.useState('1')

  const { imageUrl, loading } = useFetchImageUrl(
    'images',
    eventData.eventDetails.imageFilename,
    'event',
    open,
  )

  useEffect(() => {
    const fetchTeamMember = async () => {
      if (
        open &&
        eventData.eventDetails?.createdBy?.workdayId &&
        eventData.eventDetails?.createdBy?.email
      ) {
        try {
          await dispatch(
            fetchTeamMemberInfo({
              workdayId: eventData.eventDetails.createdBy.workdayId,
              email: eventData.eventDetails.createdBy.email,
            }),
          )
        } catch (error) {
          console.error('Failed to fetch team member info:', error)
        }
      }
    }

    fetchTeamMember()
  }, [
    open,
    eventData,
    eventData?.eventDetails.createdBy?.workdayId && eventData?.eventDetails.createdBy?.email,
  ])

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    if (openModal) {
      navigate('/events/event')
    }
    setOpen(false)
    dispatch(clearTeamMemberInfo())
  }

  useEffect(() => {
    if (openModal) {
      setOpen(true)
    }
  }, [openModal])

  return (
    <React.Fragment>
      {!openModal && (
        <Button
          onClick={handleOpen}
          id='view-event'
          variant='contained'
          color='success'
          startIcon={<RemoveRedEyeRoundedIcon fontSize='inherit' />}
          fullWidth
        >
          View
        </Button>
      )}

      <Dialog
        open={open}
        aria-labelledby='event-dialog-title'
        fullWidth={!fullScreen}
        maxWidth='lg'
        fullScreen={fullScreen}
      >
        {loading ? (
          <DialogSkeleton />
        ) : eventData ? (
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box position='relative' sx={{ height: '500px', overflow: 'hidden' }}>
                <CardMedia
                  component='img'
                  image={imageUrl ? imageUrl : DefaultEventImage}
                  height='100%'
                  alt='event-card-preview'
                  sx={{ objectFit: 'cover', zIndex: 0 }}
                />

                <Box
                  position='absolute'
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  p={2}
                  sx={{
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'end',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    color: textColors.white,
                    fontSize: fontSizes.size16,
                    fontWeight: fontWeights[700],
                  }}
                >
                  {eventData.eventDetails.title}
                </Box>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                }}
                size='small'
              >
                <CloseIcon />
              </IconButton>
              <Box p={2}></Box>
              <Box sx={{ width: '100%' }}>
                <TabContext value={value}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleChange} aria-label='Event tabs' centered>
                      <Tab label='Event Details' value='1' />
                      <Tab label='Event Share: Link & QR' value='2' />
                    </TabList>
                  </Box>
                  <CustomizeTabPanel value='1' fullScreen={fullScreen}>
                    <EventDetailsTab eventDetail={eventData} onClose={handleClose} />
                  </CustomizeTabPanel>
                  <CustomizeTabPanel value='2' fullScreen={fullScreen}>
                    <EventShareTab eventDetail={eventData.eventDetails} />
                  </CustomizeTabPanel>
                </TabContext>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Dialog
            open={open}
            aria-labelledby='event-dialog-title'
            fullWidth={!fullScreen}
            maxWidth='lg'
            fullScreen={fullScreen}
          >
            No Data
          </Dialog>
        )}
      </Dialog>
    </React.Fragment>
  )
}

export default EventViewModal
